import {useState} from "react";
import ErrorUtils from "src/utils/ErrorUtils";
import * as XLSX from 'xlsx';
import {JEStatusExcelColumns} from "src/components/reports/jeStatusDashboard/Constants";
import StringUtils from "src/utils/stringUtils";
import {CLIExecutionRecord} from "src/models/reports/GetCLIExecutionRecords";
import {downloadReport} from "src/components/reports/downloadReports/Utils";

// Custom header overrides for columns where camelCase conversion isn't suitable
const CUSTOM_EXCEL_HEADERS: Record<string, string> = {
    pliStatus: "PLI Status",
    pliReason: "PLI Failure Reason"
};

function prepareJEPostingStatusDataForExcel(data: CLIExecutionRecord[]): any[] {
    if (!data?.length) return [];
    
    const result: any[] = [];

    // Pre-compute column transformations to avoid repeated string operations
    const columnTransforms = JEStatusExcelColumns.map(column => ({
        original: column,
        camelCase: CUSTOM_EXCEL_HEADERS[column] || StringUtils.convertCamelCaseToTitleCase(column)
    }));
    
    // Map CLI and JE fields to their respective data sources with null safety
    const columnMapping: Record<string, (cli: any, je?: any) => any> = {
        cliBaseAmount: (cli: any) => cli.baseAmount ?? '',
        cliMarkupAmount: (cli: any) => cli.markupAmount ?? '',
        cliError: (cli: any) => cli.jeError ?? '',
        jeBaseAmount: (_, je: any) => je?.baseAmount ?? '',
        jeMarkupAmount: (_, je: any) => je?.markupAmount ?? '',
        jeError: (_, je: any) => je?.jeError ?? '',
        jeTagId: (_, je: any) => je?.jeTagId ?? ''
    };
    
    data.forEach((cliRow: any) => {
        if (!cliRow) return; // Skip null CLI rows
        
        // Create one Excel row per JE subrow, or one row if no subrows exist
        const subRows = cliRow.subRows?.length > 0 ? cliRow.subRows : [null];
        
        subRows.forEach((jeRow: any) => {
            const formattedRow: Record<string, any> = {};
            
            // Build Excel row with CLI data repeated for each JE subrow
            columnTransforms.forEach(({ original, camelCase }) => {
                const mapper = columnMapping[original];
                formattedRow[camelCase] = mapper 
                    ? mapper(cliRow, jeRow || undefined)  // Use mapped CLI/JE data
                    : cliRow[original] ?? '';             // Use CLI data for other columns
            });
            
            result.push(formattedRow);
        });
    });
    
    return result;
}

export function useJEPostingDashboardExcelBasedDownload(sheetName: string, fileName: string) {
    const [downloadResult, setDownloadResult] = useState<string>('');
    const [downloadError, setDownloadError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const downloadJEPostingStatusReportExcel = (jePostingStatusRecordsToDownload: any[]) => {
        setLoading(true);
        setDownloadResult('');
        setDownloadError('');

        try {
            if (jePostingStatusRecordsToDownload.length > 0) {
                const formattedJEPostingStatusData = prepareJEPostingStatusDataForExcel(jePostingStatusRecordsToDownload);
                const workSheet = XLSX.utils.json_to_sheet(formattedJEPostingStatusData);
                const workBook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workBook, workSheet, sheetName);
                const wbOut = XLSX.write(workBook, { bookType: 'xlsx', type: 'base64' });
                downloadReport(fileName, wbOut);
                setDownloadResult('Success');
            } else {
                setDownloadResult('No records to download.');
            }
        } catch (error) {
            setDownloadError(ErrorUtils.getMessage(error));
        } finally {
            setLoading(false);
        }
    };

    return [downloadResult, loading, downloadError, downloadJEPostingStatusReportExcel] as const;
}
